<script setup lang="ts">
import type { UiIconName } from "@/components/UiIcon.vue";

import { computed, onMounted, onUpdated, useSlots, useTemplateRef } from "vue";

import UiIcon from "@/components/UiIcon.vue";

/**
 * 접는 블록(`details` / `summary`) 한 가지(69차 ux-a §4.3). 화면별 ▾ 글자 · webkit 표지 숨김 재구현을 대체한다.
 * - 표지는 오른쪽 끝 선 화살표(UiIcon chevron-down, 열리면 180도). 브라우저 기본 삼각형은 숨긴다.
 * - 제목은 `summary` 안 h2/h3 이다(HTML 명세가 summary 안 제목 요소를 허용한다). 펼침 상태는 details 가 화면 읽기에 직접 알린다.
 * - `v-model:open`(기본 닫힘). 접힌 내용도 브라우저 찾기(Ctrl+F)에 걸리면 열린다 — 그때도 toggle 로 v-model 이 따라간다.
 * - variant: card = 섹션 카드(`.card.ui-card`), subbox = 카드 안 하위 상자(`.ui-subbox`), plain = 상자 없음.
 * - 80차 `variant="box"`(UXS80-03 · C3 · 리드 결정 L9): **카드 안 하위 묶음**용 — 테두리 없이 옅은 회색 면(`--ui-neutral-bg`)만 둔 상자. 선 있는 `subbox` 가 카드 안에서 또 선 상자(상자 안 상자)를 만들던 자리.
 *   제목은 subbox 와 같은 14/800, 펼친 내용과 제목 사이는 선 없이 간격만. 전역 `.ui-subbox`(`!important` 다수)와 섞이지 않게 `ui-subbox` 클래스를 달지 않는다.
 *   깊이는 카드 > box 한 겹까지만 — box 안에 또 box · subbox 를 두지 않는다(DEV 경고 `[ui-box-depth]`, 렌더는 그대로).
 * - 80차 요약 글 배치 한 가지(ux-c C3 · UXC80-27): `summaryMeta` 는 늘 **제목 줄 오른쪽**, 13px · 보통 굵기 · `--ui-text-2` 한 모양이다(오른쪽 굵게 / 아래 왼쪽 회색 / 없음이 화면마다 달랐다).
 *   ≤620 은 제목 아래 전폭 줄(왼쪽 정렬). 제목 아래 따로 요약 문장을 그리던 호출부는 `summaryMeta` 로 옮긴다.
 * - 80차 `inline`(plain 전용 — ux-b C5 · UXB80-27): 요약 줄이 글 폭만큼만(`fit-content`) — 꺾쇠가 제목(·요약) **바로 뒤**에 붙는다(「확정된 상태 · 고치면 확정이 풀립니다 · 자세히 ⌄」 한 줄).
 *   기본(전폭 줄 — 꺾쇠가 오른쪽 끝)은 그대로. 펼친 내용은 이 줄 아래 전폭이다.
 * - 82차 UXB82-10(b-C1): ≤620 에서 `summaryMeta` 가 짧으면(≤ `META_SHORT_MAX` 10자 — 「5 / 5」 · 「3건」 · 「68자」) 제목 줄 안 ⌄ 앞에 둔다. 부품이 `is-meta-short` 를 붙이므로 prop 은 없다.
 *   긴 문장 meta 는 지금대로 제목 아래 전폭 줄. 1440 은 변화 없음(원래 한 줄). 한 화면의 접힘 줄 수 × 약 30px 가 빠진다.
 * - 83차 UXB83-10(b-C3): ≤620 요약(meta)의 자리를 **한 규칙**으로 정한다 — 부제가 있으면(`summary` 슬롯으로 제목 아래 부제를 그린 card · subbox · box) meta 는 부제 아래 둘째 줄,
 *   부제가 없고 짧으면 제목 줄 ⌄ 앞, 길면 제목 아래 둘째 줄. 짧은 meta 가 부제 줄 옆에 끼어 제목 칸을 150px 로 줄여 부제를 3줄로 꺾던 것을 막는다. prop 은 없다.
 * - 82차 UXB82-27: plain 의 오른쪽 안쪽 여백은 이미 0 이다(계산값 `padding-right 0` · ⌄ 오른쪽 = 접힘 오른쪽, 직무분석 12개 계측) — 「근거 보기」 가 13px 안쪽에 선 것은
 *   이 부품이 아니라 그 줄의 `is-quiet` 버튼 안쪽 여백이다(버튼 몫 — 부품 변경 없음).
 * - 82차 sys-C8(UXS82-10): 펼친 내용의 **바로 아래 문단**(`> p`)은 줄 길이 상한 `--ui-measure`(72ch)를 따른다 — 안내 글이 카드 폭(약 1000px)을 다 쓰던 것. 표 · 폼 · 격자는 상한이 없다.
 * - 89차 C89-08(UXS89-01): `card` · `subbox` · `box` 는 **누르는 영역이 보이는 상자 전체**다 — 안쪽 여백(`--disc-pad`)에 있던 가장자리 클릭이 안 먹던 것을 `summary::before`(여백만큼 바깥으로 편
 *   투명 면)가 받는다. 레이아웃 · 보이는 크기는 그대로(여백을 summary 로 옮기지 않고 눌리는 면만 넓힌다 — 전역 `.card` · `.ui-subbox` 여백이 `!important` 라 부품이 못 옮긴다).
 *   호버 면은 `card` 만(토큰 `--ui-fold-hover-card` — 값은 main.scss. 선 줄(`box`)은 면을 칠하지 않아 92차 C92-04 가 `--ui-fold-hover-box` 를 지웠다). `subbox` 는 면이 이미 옅어 ⌄ 색만.
 * - 89차 C89-08(UXS89-04): **`plain` 은 안내 전용**이다. `input` · `textarea` · `FormField` 를 담으면 DEV 경고 `[ui-plain-input]` — 입력 · 데이터는 `box` · `subbox`.
 * - 89차 C89-29: `wrapper`(plain 전용) — plain 이 **여러 카드 · 접힘을 통째로 감싸는** 바깥 접힘(면접관 면접준비 「면접키트 전체 보기」)이면 안쪽 입력이 곧 안쪽 카드의 것이라 `[ui-plain-input]` 경고를 면제한다.
 *   안쪽을 box 로 바꾸면 접힘 안 접힘(`[ui-box-depth]`)이라 바깥을 plain 으로 두는 것이 맞는 자리 전용 — 입력을 직접 담는 plain 에는 쓰지 않는다(그건 box · subbox).
 * - 89차 C89-32: 아이콘(`icon`)을 준 card · subbox · box 는 아이콘이 **제목 앞 같은 줄**에 선다(전엔 head 격자가 제목 위 줄에 쌓았다). kicker 가 함께 있으면 아이콘은 kicker · 제목 두 줄 왼쪽 가운데.
 * - 89차 C89-08(UXA89-22): ≤620 에서 `plain` 의 요약(meta)이 제목 아래 둘째 줄로 내려가면 줄 아래 8px 를 둔다(다음 줄의 위 선에 붙던 것).
 * - 89차 UXB89-22 · UXB83-20(`plain` 오른쪽 상쇄): 이미 0 이다(안쪽 여백 0 · 계산값 확인) — 「근거 보기」 가 안쪽에 선 것은 이 부품이 아니라 `LoadingButton quiet` 의 안쪽 여백이라 부품 변경 없음.
 * - 90차 C90-23(UXA90-04 b): `flushBody`(box 전용, 기본 false) — 펼친 내용이 상자 안쪽 여백(`--ui-pad-subbox`)을 쓰지 않고 상자 가장자리까지 편다(좌 · 우 · 아래 여백을 음수로 상쇄). 「자세히 보기」 안 A · B · C 카드가 상자 안쪽 16px 에 서서
 *   바깥 카드(설계 영역 현황)보다 좁던 것 — 상자는 **머리 줄만** 회색 면이고 펼친 내용(카드 · 표)은 바깥 카드와 같은 x. 접힌 모양 · 머리 줄은 그대로. 펼친 내용이 스스로 면(카드)을 가질 때만 쓴다(글만 있으면 회색 면이 사라져 상자가 허공에 뜬다).
 * - 93차 C93-33(UXS93-14): `headless`(plain 전용) — 제목을 `h2` · `h3` 가 아니라 글(`span`)로 그린다. 「이 화면 안내」 처럼 문서 개요(제목 목록)에 올리면 안 되는 안내 줄 전용이고, 호출부는 직접 쓰지 않고 `PageGuide` 를 쓴다.
 * - 78차(UXA78-14 · UXB78-21) 제목 위계: 접힘 제목은 카드 소제목(`SectionHead level 3` 16/800)보다 작아야 한다.
 *   `plain` = **보조 안내 글**(13/700 `--ui-text-2` + 앞 `info` 아이콘 14 + 오른쪽 chevron) — 「이 단계에서 하는 일」 이 아래 입력칸의 제목처럼 읽히던 것.
 *   `subbox` = 14/800. `card` 는 카드 자체라 20/900 그대로. 아이콘은 `icon` 으로 바꾸거나 `:icon="false"` 로 끈다(`plain` 만 기본 `info`).
 *   제목은 늘 `h2`/`h3` 요소다 — 프로토타입 `.card h3{font-size:17px !important}` 가 걷혀(78차 ds-css) 부품 값이 이긴다(전엔 `div role="heading"` 으로 비껴 갔다).
 * - **91차 UXS91-01 — 피부 두 가지로(기본 모양이 바뀜 · 클래스 이름 · prop 불변)**: 접힘이 테두리 글 버튼 · 회색 알약 줄 · 카드 + 꺾쇠 · 글 「자세히」 · 선 줄 다섯 모양이었다.
 *   ① **구획 접힘 = `card`**: 머리 줄 전체가 누름 대상(위 C89-08 면 그대로), 오른쪽 끝 꺾쇠는 28px 둥근 칸(`.ui-fold-chevron` — 전역 정본이라 한 방향 접힘 줄
 *   `JobAnalysisStepFold` 도 같은 칸을 쓴다). 글 버튼은 두지 않는다.
 *   ② **안내 · 근거 · 하위 목록 접힘 = 선 줄**: `box` · `subbox` 는 회색 면 · 선 상자가 아니라 **위 구분선 줄(아래 선 없음) + 좌우 여백 0**이다(원칙 4 — 카드 안 셋째 면을 걷는다).
 *   제목 14/700 `--ui-text` · 요약 13/500 · 꺾쇠 20. 펼친 내용은 제목 줄 아래 12px, 안쪽 구분선 없음. `subbox` 는 전역 `.ui-subbox` 클래스를 그대로 달지만 상자 피부는 받지 않는다
 *   (main.scss `.ui-subbox:where(:not(.ui-disclosure))` — 안쪽 안내 평탄화 · 지표 strip 같은 「하위 상자 안」 규칙은 그대로 산다). `flushBody` 는 좌우 여백이 0 이 되어 할 일이 없다(prop 유지 · 무해).
 *   `plain` 은 안내 글 피부(13/700 `--ui-text-2` · ⓘ) 그대로다. ③ **행 토글 = `plain` + `inline`**(80차) — 꺾쇠가 글 바로 뒤(빠른 채용 「자세히」 행 토글 자리).
 */
const props = withDefaults(
  defineProps<{
    /** 제목 */
    title: string;
    /** 제목 위 짧은 분류(「참고」, 「STEP 02」) */
    kicker?: string;
    /** 접힌 줄 오른쪽 요약(「산출물: …」, 「3건」) */
    summaryMeta?: string;
    /** card = 섹션 카드(구획 접힘), subbox · box = 카드 안 하위 묶음(91차부터 둘 다 선 줄 — 위 구분선), plain = 안내 글 줄 */
    variant?: "card" | "subbox" | "box" | "plain";
    /** 제목 수준(h2 · h3). 주지 않으면 3 */
    level?: 2 | 3;
    /** 제목 앞 아이콘(14). 주지 않으면 plain 만 `info`, `false` 면 없음 */
    icon?: UiIconName | false;
    /** plain 전용: 요약 줄을 글 폭만큼만 — 꺾쇠가 글 바로 뒤에 붙는다 */
    inline?: boolean;
    /** box 전용: 펼친 내용을 상자 안쪽 여백 없이 가장자리까지(머리 줄만 회색 면, 안 카드는 바깥 카드와 같은 x) */
    flushBody?: boolean;
    /** plain 전용: 여러 카드 · 접힘을 통째로 감싸는 바깥 접힘 — `[ui-plain-input]` 경고 면제(C89-29) */
    wrapper?: boolean;
    /** 제목을 `h2` · `h3` 가 아니라 글(`span`)로 — 문서 개요(제목 목록)에 올리지 않는 안내 줄(`PageGuide` — 93차 C93-33 · UXS93-14). `plain` 전용 */
    headless?: boolean;
  }>(),
  {
    kicker: undefined,
    summaryMeta: undefined,
    variant: "card",
    level: undefined,
    icon: undefined,
    inline: false,
    flushBody: false,
    wrapper: false,
    headless: false,
  },
);

defineSlots<{
  /** 펼친 내용 */
  default?: () => unknown;
  /** 제목 줄을 직접 그릴 때(표지 화살표는 그대로 붙는다) */
  summary?: () => unknown;
}>();

/** 펼침 */
const open = defineModel<boolean>("open", { default: false });

const VARIANT_CLASSES: Record<NonNullable<typeof props.variant>, string[]> = {
  card: ["card", "ui-card", "is-card"],
  subbox: ["ui-subbox", "is-subbox"],
  box: ["is-box"],
  plain: ["is-plain"],
};

/** ≤620 에서 같은 줄에 둘 수 있는 짧은 요약의 글자 수 상한 */
const META_SHORT_MAX = 10;
const slots = useSlots();
/**
 * 제목 아래 부제가 있는가(UXB83-10). `summary` 슬롯을 쓰는 card · subbox · box 는 제목 + 부제를 손수 그린 머리다.
 * plain 은 안내 글 한 줄(「보내는 내용 보기」)이라 슬롯을 써도 부제가 아니다.
 */
const hasSubtitle = computed(
  () => !!slots.summary && props.variant !== "plain",
);
const metaShort = computed(
  () =>
    !!props.summaryMeta &&
    props.summaryMeta.trim().length <= META_SHORT_MAX &&
    !hasSubtitle.value,
);

const headingTag = computed(() =>
  props.headless && props.variant === "plain" ? "span" : `h${props.level ?? 3}`,
);
const iconName = computed<UiIconName | undefined>(() => {
  if (props.icon === false) return undefined;
  return props.icon ?? (props.variant === "plain" ? "info" : undefined);
});

const root = useTemplateRef<HTMLElement>("root");

if (import.meta.env.DEV) {
  if (props.flushBody && props.variant !== "box")
    console.warn(
      `[Disclosure] flushBody 는 variant="box" 에서만 — variant="${props.variant}" 는 무시합니다 (${props.title})`,
    );
  if (props.inline && props.variant !== "plain")
    console.warn(
      `[Disclosure] inline 은 variant="plain" 에서만 — variant="${props.variant}" 는 무시합니다 (${props.title})`,
    );
  // 상자 깊이(UXS80-03 · C3): 카드 > box 한 겹까지. box · subbox 안에 또 box · subbox 가 있으면 한 번 알린다.
  onMounted(() => {
    if (props.variant !== "box" && props.variant !== "subbox") return;
    const outer = root.value?.parentElement?.closest(
      ".ui-disclosure.is-box, .ui-disclosure.is-subbox",
    );
    if (outer)
      console.warn(
        `[ui-box-depth] 접힘 상자 안에 또 접힘 상자(${props.variant}) — 카드 > 하위 묶음 한 겹까지만, 안쪽은 variant="plain" 으로 (${props.title})`,
      );
  });
  // `plain` 은 안내 전용(UXS89-04 · C89-08 ④): 입력 · 데이터를 담으면 한 번 알린다. 안쪽 접힘 상자가 가진 입력은 그 상자 몫이라 세지 않는다.
  let warnedPlainInput = false;
  const warnPlainInput = (): void => {
    if (warnedPlainInput || props.variant !== "plain" || props.wrapper) return;
    const el = root.value;
    if (!el) return;
    const found = [
      ...el.querySelectorAll(
        ".ui-disclosure-body :is(input:not([type=hidden]), textarea, .ui-form-field)",
      ),
    ].some((node) => node.closest(".ui-disclosure") === el);
    if (!found) return;
    warnedPlainInput = true;
    console.warn(
      `[ui-plain-input] plain 접힘 안에 입력(input · textarea · FormField) — plain 은 안내 전용, 입력 · 데이터를 담는 접힘은 variant="box" 또는 "subbox" (${props.title})`,
    );
  };
  onMounted(warnPlainInput);
  onUpdated(warnPlainInput);
}

function onToggle(event: Event): void {
  const el = event.currentTarget;
  if (el instanceof HTMLDetailsElement && el.open !== open.value) {
    open.value = el.open;
  }
}
</script>

<template>
  <details
    ref="root"
    class="ui-disclosure"
    :class="[
      VARIANT_CLASSES[variant],
      {
        'is-inline': inline && variant === 'plain',
        'is-flush-body': flushBody && variant === 'box',
        'is-meta-short': metaShort,
      },
    ]"
    :open="open"
    @toggle="onToggle"
  >
    <summary class="ui-disclosure-summary">
      <span class="ui-disclosure-head">
        <slot name="summary">
          <UiIcon
            v-if="iconName"
            class="ui-disclosure-icon"
            :name="iconName"
            :size="14"
          />
          <span v-if="kicker" class="ui-disclosure-kicker">{{ kicker }}</span>
          <component :is="headingTag" class="ui-disclosure-title">
            {{ title }}
          </component>
        </slot>
      </span>
      <span v-if="summaryMeta" class="ui-disclosure-meta">
        {{ summaryMeta }}
      </span>
      <span
        v-if="variant === 'card'"
        class="ui-fold-chevron"
        aria-hidden="true"
      >
        <UiIcon class="ui-disclosure-chevron" name="chevron-down" :size="20" />
      </span>
      <UiIcon
        v-else
        class="ui-disclosure-chevron"
        name="chevron-down"
        :size="20"
      />
    </summary>
    <div class="ui-disclosure-body">
      <slot />
    </div>
  </details>
</template>

<style scoped>
.ui-disclosure-summary {
  display: flex;
  align-items: center;
  gap: var(--ui-gap-md);
  min-height: 24px;
  list-style: none;
  cursor: pointer;
}

.ui-disclosure-summary::-webkit-details-marker {
  display: none;
}

.ui-disclosure-summary::marker {
  content: "";
}

.ui-disclosure-head {
  display: grid;
  flex: 1 1 auto;
  gap: 2px;
  min-width: 0;
}

/* 아이콘을 준 card · subbox · box(C89-32) — [아이콘 | 제목] 한 줄, kicker 가 있으면 아이콘은 두 줄 왼쪽에 걸친다. plain 은 이미 flex 한 줄 */
.ui-disclosure:not(.is-plain) .ui-disclosure-head:has(> .ui-disclosure-icon) {
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 6px;
  align-items: center;
}

.ui-disclosure:not(.is-plain)
  .ui-disclosure-head:has(> .ui-disclosure-icon)
  > .ui-disclosure-icon {
  grid-row: 1 / span 2;
}

.ui-disclosure:not(.is-plain)
  .ui-disclosure-head:has(> .ui-disclosure-icon):not(
    :has(> .ui-disclosure-kicker)
  )
  > .ui-disclosure-icon {
  grid-row: 1;
}

.ui-disclosure-kicker {
  color: var(--ui-text-3);
  font-size: var(--ui-fs-xs);
  font-weight: 800;
  letter-spacing: 0.02em;
}

.ui-disclosure-title {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-lg);
  font-weight: 800;
  line-height: 1.4;
  word-break: keep-all;
  overflow-wrap: break-word;
}

.ui-disclosure.is-card .ui-disclosure-title {
  font-size: var(--ui-fs-xl);
  font-weight: 900;
}

/* 78차: subbox 는 카드 소제목(16/800)보다 한 단 작게, plain 은 제목이 아니라 보조 안내 글. 91차 선 줄 피부는 14/700 */
.ui-disclosure:is(.is-subbox, .is-box) .ui-disclosure-title {
  font-size: var(--ui-fs-md);
  font-weight: 700;
}

/*
 * 91차 UXS91-01 ② 선 줄(box · subbox) — 면 · 반경 없이 **위 구분선 하나**, 좌우 여백 0(카드 글 시작선과 같은 x). 아래 선은 두지 않는다 — 접힘 줄이
 * 서로 다른 부모(래퍼)에 나뉘어 있으면 「아래 선 + 다음 줄 위 선」 이 두 겹으로 보였다(면접준비 키트 3줄 캡처). 묶음 끝은 카드 가장자리 · 다음 요소가 닫는다. subbox 의 전역 `.ui-subbox` 상자 피부는 main.scss 가 접힘에서 빼므로(`:where(:not(.ui-disclosure))`) 중요도 없이 값이 이긴다.
 */
.ui-disclosure:is(.is-box, .is-subbox) {
  --disc-pad-y: 14px;

  padding: var(--disc-pad-y) 0;
  border-top: 1px solid var(--ui-line);
  border-radius: 0;
  background: transparent;
}

/*
 * 93차 C93-34(UXS93-03 · UXC93-16): 구획 머리(`SectionHead` · `.ui-subhead` — 아래 구분선이 이미 있다) **바로 아래** 접힘 줄은 윗선을 그리지 않는다 — 머리 구분선과 접힘 윗선이 16px 간격 두 줄이었다
 * (AI 리터러시 · 입사지원서 · 관리자 · 공정채용 · 결과보고서 5곳). 같은 명시도 · 뒤 선언으로 이긴다(새 `!important` 0). 접힘이 머리 바로 아래가 아니면(사이에 다른 요소) 윗선 그대로다.
 */
:is(.ui-section-head, .ui-subhead) + .ui-disclosure:is(.is-box, .is-subbox) {
  border-top: 0;
}

/* 92차 C92-24(UXB92-09): 이어 선 접힘 줄(`box`) 형제 사이는 한 값(`--ui-fold-gap` 8 — main.scss 토큰) — 화면마다 scoped `margin-bottom` 8 · 12 · 18 · 24 · 42 로 갈라지던 세로 피치를 부품이 정한다. 호출부는 자기 `margin-bottom` 을 걷는다. */
.ui-disclosure.is-box + .ui-disclosure.is-box {
  margin-top: var(--ui-fold-gap, 8px);
}

.ui-disclosure:is(.is-box, .is-subbox) .ui-disclosure-meta {
  font-weight: 500;
}

.ui-disclosure:is(.is-box, .is-subbox) > .ui-disclosure-body {
  margin-top: 12px;
}

/* h3 요소로 돌아오며 전역 제목 자간이 붙지 않게 — `div` 였을 때처럼 부모 자간을 따른다(모양 변화 0) */
.ui-disclosure:is(.is-plain, .is-subbox, .is-box) .ui-disclosure-title {
  letter-spacing: inherit;
}

.ui-disclosure.is-plain .ui-disclosure-head {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ui-disclosure.is-plain .ui-disclosure-title {
  color: var(--ui-text-2);
  font-size: var(--ui-fs-sm);
  font-weight: 700;
}

.ui-disclosure.is-plain .ui-disclosure-summary:hover .ui-disclosure-title {
  color: var(--ui-text);
}

.ui-disclosure-icon {
  flex: none;
  color: var(--ui-text-3);
}

.ui-disclosure.is-plain .ui-disclosure-chevron {
  width: 16px;
  height: 16px;
}

/* 요약 글은 한 모양(80차 C3): 제목 줄 오른쪽 · 13px · 보통 굵기 · text-2 */
.ui-disclosure-meta {
  flex: 0 1 auto;
  min-width: 0;
  color: var(--ui-text-2);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-regular);
  text-align: right;
  word-break: keep-all;
}

.ui-disclosure-chevron {
  flex: none;
  color: var(--ui-text-3);
  transition: transform var(--ui-dur-fast) var(--ui-ease);
}

.ui-disclosure[open] > .ui-disclosure-summary .ui-disclosure-chevron {
  transform: rotate(180deg);
}

/* card 꺾쇠 칸(91차 ① — 28px 둥근 칸 모양은 전역 `.ui-fold-chevron` 정본). 머리 줄을 가리키면 칸이 진해진다 */
.ui-disclosure.is-card > .ui-disclosure-summary:hover .ui-fold-chevron {
  background: var(--ui-line);
}

.ui-disclosure-summary:hover .ui-disclosure-chevron {
  color: var(--ui-text);
}

/*
 * 누르는 영역 = 상자 전체(89차 C89-08 · UXS89-01). 상자 여백(`--disc-pad`)은 그대로 두고 summary 의 `::before` 가 여백만큼 바깥으로 편 면이 된다 —
 * `summary` 의 자손이라 그 면을 눌러도 접힘이 토글되고, 호버 면도 여기에 칠한다. 글 · 꺾쇠는 `z-index: -1` 위에 그대로 뜬다.
 * 열린 상태는 아래쪽으로 펴지 않는다(펼친 내용 위에 덮이면 안 된다). `--disc-pad` · `--disc-radius` 는 전역 `.card` · `.ui-subbox` 의 여백 · 반경(안쪽 반경 = 바깥 − 선 1px).
 */
.ui-disclosure.is-card {
  --disc-pad: var(--ui-pad-card);
  --disc-radius: calc(var(--ui-radius-card) - 1px);
}

.ui-disclosure:is(.is-box, .is-subbox, .is-card) > .ui-disclosure-summary {
  position: relative;
  z-index: 0;
}

.ui-disclosure.is-card > .ui-disclosure-summary::before {
  content: "";
  position: absolute;
  z-index: -1;
  inset: calc(var(--disc-pad) * -1);
  border-radius: var(--disc-radius);
}

.ui-disclosure.is-card[open] > .ui-disclosure-summary::before {
  bottom: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.ui-disclosure.is-card > .ui-disclosure-summary:hover::before {
  background: var(--ui-fold-hover-card);
}

/* 선 줄(91차)은 위아래 여백만 누름 면으로 편다 — 면 색은 칠하지 않고 제목 · 꺾쇠 색으로 호버를 알린다(plain 과 같은 결) */
.ui-disclosure:is(.is-box, .is-subbox) > .ui-disclosure-summary::before {
  content: "";
  position: absolute;
  z-index: -1;
  inset: calc(var(--disc-pad-y) * -1) 0;
}

.ui-disclosure:is(.is-box, .is-subbox)[open] > .ui-disclosure-summary::before {
  bottom: 0;
}

.ui-disclosure:is(.is-box, .is-subbox)
  > .ui-disclosure-summary:hover
  .ui-disclosure-title {
  color: var(--ui-blue-700);
}

.ui-disclosure-body {
  margin-top: 14px;
}

/* 안내 글 줄 길이(82차 sys-C8) — 바로 아래 문단만. 토큰은 ds-css 의 `--ui-measure`, 아직 없으면 72ch */
.ui-disclosure-body > :slotted(p) {
  max-width: min(100%, var(--ui-measure, 72ch));
}

/* 카드는 펼친 내용과 제목 줄 사이를 선으로 나눈다(91차: subbox 는 선 줄이라 안쪽 선 없음) */
.ui-disclosure.is-card > .ui-disclosure-body {
  padding-top: 14px;
  border-top: 1px solid var(--ui-line);
}

.ui-disclosure.is-plain > .ui-disclosure-body {
  margin-top: var(--ui-gap-sm);
}

/* plain inline(80차) — 요약 줄이 글 폭만큼. 꺾쇠는 마지막 글 바로 뒤, 요약 글(meta)은 제목 뒤에 이어 붙는다. */
.ui-disclosure.is-inline > .ui-disclosure-summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ui-gap-xs) var(--ui-gap-sm);
  width: fit-content;
  max-width: 100%;
}

.ui-disclosure.is-inline .ui-disclosure-head {
  flex: 0 1 auto;
}

.ui-disclosure.is-inline .ui-disclosure-meta {
  text-align: left;
}

/* 카드 안쪽 여백은 ≤760 에서 18(전역 `.card` 규칙 — `--ui-pad-card-sm`) */
@media (max-width: 760px) {
  .ui-disclosure.is-card {
    --disc-pad: var(--ui-pad-card-sm);
  }
}

@media (max-width: 620px) {
  /*
   * 좁은 폭은 [제목 | ⌄] 두 칸 격자 — 제목은 왼쪽 칸 안에서 꺾이고 펼침 표지는 첫 줄 오른쪽 끝에 남는다(73차 fe-quick — flex 줄바꿈이면
   * 제목 칸이 한 줄을 다 차지해 ⌄ 가 다음 줄로 떨어졌다, 빠른채용 STEP 05). 요약(meta)은 제목 아래 전폭 줄.
   */
  .ui-disclosure:where(:not(.is-inline)) > .ui-disclosure-summary {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: var(--ui-gap-xs) var(--ui-gap-md);
  }

  .ui-disclosure-head {
    grid-row: 1;
    grid-column: 1;
  }

  .ui-disclosure-summary > :is(.ui-disclosure-chevron, .ui-fold-chevron) {
    grid-row: 1;
    grid-column: 2;
    margin-top: 2px;
  }

  /* card 의 28px 칸은 제목 첫 줄(20/900 · 줄 높이 28)과 같은 높이라 위 보정이 없다 */
  .ui-disclosure-summary > .ui-fold-chevron {
    margin-top: 0;
  }

  .ui-disclosure-meta {
    grid-row: 2;
    grid-column: 1 / -1;
    text-align: left;
  }

  /* 짧은 요약(≤10자)은 제목 줄 ⌄ 앞 — [제목 | 요약 | ⌄] */
  .ui-disclosure.is-meta-short:where(:not(.is-inline))
    > .ui-disclosure-summary {
    grid-template-columns: minmax(0, 1fr) auto auto;
  }

  .ui-disclosure.is-meta-short:where(:not(.is-inline)) .ui-disclosure-meta {
    grid-row: 1;
    grid-column: 2;
    margin-top: 2px;
    text-align: right;
    white-space: nowrap;
  }

  .ui-disclosure.is-meta-short:where(:not(.is-inline))
    .ui-disclosure-summary
    > :is(.ui-disclosure-chevron, .ui-fold-chevron) {
    grid-column: 3;
  }

  /* plain 의 요약이 제목 아래 둘째 줄로 내려오면 다음 줄 위 선에 붙지 않게 아래 8px(UXA89-22) */
  .ui-disclosure.is-plain:where(:not(.is-inline, .is-meta-short))
    > .ui-disclosure-summary:has(.ui-disclosure-meta) {
    padding-bottom: 8px;
  }
}
</style>
